import * as PopoverPrimitive from '@radix-ui/react-popover';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { TargetRect } from './useWalkthroughTarget';
import type {
  WalkthroughPlacement,
  WalkthroughStep,
} from '@/services/walkthrough.service';

interface WalkthroughCardProps {
  step: WalkthroughStep;
  rect: TargetRect | null;
  stepIndex: number;
  totalSteps: number;
  showProgress: boolean;
  allowSkip: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
}

const SIDE_BY_PLACEMENT: Record<
  WalkthroughPlacement,
  'top' | 'bottom' | 'left' | 'right'
> = {
  auto: 'bottom',
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
};

function CardBody({
  step,
  stepIndex,
  totalSteps,
  showProgress,
  allowSkip,
  onPrev,
  onNext,
  onSkip,
}: Omit<WalkthroughCardProps, 'rect'>) {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const progress = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          {showProgress && (
            <p className="text-muted-foreground text-xs font-medium">
              Passo {stepIndex + 1} de {totalSteps}
            </p>
          )}
          <h3 className="text-base font-semibold leading-tight">
            {step.title}
          </h3>
        </div>

        {allowSkip && (
          <Button variant="ghost" size="icon-sm"
            type="button"
            onClick={onSkip}
            aria-label="Pular tutorial"
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {step.imageUrl && (
        <img
          src={step.imageUrl}
          alt=""
          className="border-border/50 max-h-40 w-full rounded-lg border object-cover"
        />
      )}

      <p className="text-muted-foreground text-sm leading-relaxed">
        {step.description}
      </p>

      {showProgress && (
        <div className="bg-muted h-1 w-full overflow-hidden rounded-full">
          <motion.div
            className="bg-primary h-full rounded-full"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        {allowSkip ? (
          <Button variant="ghost" size="sm"
            type="button"
            onClick={onSkip}

          >
            Pular tutorial
          </Button>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrev}
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          )}
          <Button size="sm" onClick={onNext} className="gap-1">
            {isLast ? (
              <>
                Concluir
                <Check className="h-4 w-4" />
              </>
            ) : (
              <>
                Próximo
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

const CARD_CLASSES =
  'bg-card text-card-foreground border-border/60 w-[min(22rem,calc(100vw-2rem))] rounded-xl border p-5 shadow-2xl';

/**
 * Card explicativo do passo.
 *
 * Com alvo, é posicionado por um Popover do Radix ancorado no rect medido —
 * ganhando flip e collision detection de graça (o card nunca sai da tela em
 * viewport pequena). Sem alvo, vira um modal centralizado.
 */
export function WalkthroughCard({ rect, ...props }: WalkthroughCardProps) {
  // Passo sem alvo (ou alvo não encontrado): modal centralizado
  if (!rect) {
    return (
      <div className="pointer-events-none fixed inset-0 z-[101] flex items-center justify-center p-4">
        <motion.div
          role="dialog"
          aria-label={props.step.title}
          className={cn(CARD_CLASSES, 'pointer-events-auto')}
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.2 }}
        >
          <CardBody {...props} />
        </motion.div>
      </div>
    );
  }

  const side = SIDE_BY_PLACEMENT[props.step.placement] ?? 'bottom';

  return (
    <PopoverPrimitive.Root open>
      {/* Âncora invisível sobreposta ao alvo medido */}
      <PopoverPrimitive.Anchor asChild>
        <div
          className="pointer-events-none fixed z-[101]"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      </PopoverPrimitive.Anchor>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align="center"
          sideOffset={props.step.padding + 12}
          collisionPadding={16}
          avoidCollisions
          role="dialog"
          aria-label={props.step.title}
          className={cn(CARD_CLASSES, 'z-[101]')}
          // O tour controla a navegação; não fechar por clique fora nem Esc
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <CardBody {...props} />
          <PopoverPrimitive.Arrow className="fill-card" width={12} height={6} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
